import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { chordFromEvent, isTypingTarget, type Chord } from '@/lib/keys';
import {
  ShortcutContext,
  type ShortcutBinding,
  type ShortcutRegistry,
} from './shortcutContext';

interface ShortcutProviderProps {
  children: ReactNode;
}

/**
 * ONE `keydown` listener for the entire app.
 *
 * Registering a listener per shortcut would mean dozens of handlers running on
 * every keystroke while typing a message. Here every keystroke costs a single
 * string build plus one Map lookup.
 */
export function ShortcutProvider({ children }: ShortcutProviderProps) {
  const bindings = useRef(new Map<Chord, ShortcutBinding[]>());

  const registry = useMemo<ShortcutRegistry>(
    () => ({
      register: (binding) => {
        const existing = bindings.current.get(binding.chord) ?? [];
        // Last registered wins, so a modal can shadow a global chord.
        bindings.current.set(binding.chord, [...existing, binding]);
        return () => {
          const current = bindings.current.get(binding.chord);
          if (!current) return;
          const next = current.filter((item) => item !== binding);
          if (next.length === 0) bindings.current.delete(binding.chord);
          else bindings.current.set(binding.chord, next);
        };
      },
      list: () => Array.from(bindings.current.values()).flatMap((group) => group.slice(-1)),
    }),
    [],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.repeat) return;
      const chord = chordFromEvent(event);
      if (!chord) return;

      const group = bindings.current.get(chord);
      const binding = group?.[group.length - 1];
      if (!binding) return;
      if (!binding.allowInInput && isTypingTarget(event.target)) return;

      if (binding.preventDefault !== false) {
        event.preventDefault();
        event.stopPropagation();
      }
      binding.handler(event);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return <ShortcutContext.Provider value={registry}>{children}</ShortcutContext.Provider>;
}
